/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Image,
  TextInput,
  FlatList,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import { useRecipientSearch, startConversation } from '../../hooks/useChat';

const initialAvatar = { uri: 'https://via.placeholder.com/80/0e4e96/FFFFFF?text=%20' };
// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

export default function NewMessageScreen({ navigation, route }) {
  // ── Profile integration ──
  const profileCtx = useProfile();

  const [searchQuery,        setSearchQuery]        = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [messageText,        setMessageText]        = useState('');
  const [sending,            setSending]            = useState(false);

  // Open-inbox semantics: don't pre-filter by company; show every business_user
  // we have access to. Pass null to drop the company_id filter.
  const { recipients, loading, error } = useRecipientSearch(searchQuery, {
    companyId: null,
    debounceMs: 250,
  });

  // Pre-select a contact if we were opened from a profile page with { recipient_user_id, recipient_role }.
  useEffect(() => {
    if (route?.params?.recipient_user_id && route?.params?.recipient_role === 'business') {
      const pre = {
        id:           route.params.recipient_user_id,
        full_name:    route.params.recipient_name || 'Recipient',
        job_title:    route.params.recipient_title || '',
        company_name: route.params.recipient_company || '',
        avatar:       route.params.recipient_avatar || null,
        company_id:   route.params.recipient_company_id || null,
      };
      setSelectedRecipients([pre]);
    }
  }, [route?.params]);

  const filteredRecipients = recipients; // server-side filtered

  const toggleRecipient = (contact) => {
    const exists = selectedRecipients.some(r => String(r.id) === String(contact.id));
    if (exists) {
      setSelectedRecipients(selectedRecipients.filter(r => String(r.id) !== String(contact.id)));
    } else {
      setSelectedRecipients([...selectedRecipients, contact]);
    }
  };

  const sendMessage = async () => {
    const body = messageText.trim();
    if (!body || selectedRecipients.length === 0 || sending) return;
    setSending(true);

    const results = await Promise.allSettled(
      selectedRecipients.map(r =>
        startConversation({
          recipient_role:    'business',
          recipient_user_id: r.id,
          company_id:        r.company_id,
          initial_message:   body,
        })
      )
    );
    setSending(false);

    const ok   = results.filter(r => r.status === 'fulfilled');
    const fail = results.filter(r => r.status === 'rejected');

    if (fail.length === 0) {
      // Navigate to the first conversation we created, so the user can keep talking.
      const first = ok[0].value;
      navigation.replace('ChatDetail', {
        conversation: {
          id:          first.conversation_id,
          company:     selectedRecipients[0].company_name || selectedRecipients[0].full_name,
          position:    selectedRecipients[0].job_title || '',
          logo:        selectedRecipients[0].avatar ? { uri: selectedRecipients[0].avatar } : null,
          otherRole:   'business',
          otherUserId: selectedRecipients[0].id,
          companyId:   selectedRecipients[0].company_id,
        },
      });
    } else if (ok.length === 0) {
      Alert.alert(
        'Could not send message',
        (fail[0].reason && fail[0].reason.message) || 'Please try again in a moment.',
      );
    } else {
      Alert.alert(
        'Partly sent',
        `Sent to ${ok.length} of ${selectedRecipients.length} recipients. You can retry the failed ones from the inbox.`,
      );
      navigation.goBack();
    }
  };

  const renderRecipientItem = ({ item }) => {
    const isSelected = selectedRecipients.some(r => String(r.id) === String(item.id));
    const avatarUri = item.avatar && typeof item.avatar === 'string' && item.avatar.startsWith('http')
      ? { uri: item.avatar }
      : initialAvatar;

    return (
      <TouchableOpacity
        style={[styles.recipientItem, isSelected && styles.selectedRecipientItem]}
        onPress={() => toggleRecipient(item)}
      >
        <View style={styles.recipientAvatar}>
          <Image source={avatarUri} style={styles.avatarImage} />
        </View>

        <View style={styles.recipientInfo}>
          <Text style={styles.recipientName}>{item.full_name || 'Recipient'}</Text>
          <Text style={styles.recipientRole}>
            {item.job_title || 'Member'}
            {item.company_name ? ` at ${item.company_name}` : ''}
          </Text>
        </View>

        <View style={styles.recipientSelection}>
          {isSelected ? (
            <View style={styles.selectedCheckbox}>
              <Ionicons name="checkmark" size={16} color="#FFFFFF" />
            </View>
          ) : (
            <View style={styles.checkbox} />
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const renderSelectedRecipients = () => {
    if (selectedRecipients.length === 0) return null;
    return (
      <View style={styles.selectedRecipientsContainer}>
        <Text style={styles.selectedRecipientsLabel}>To:</Text>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.selectedRecipientsList}
        >
          {selectedRecipients.map(recipient => {
            const avatarUri = recipient.avatar && typeof recipient.avatar === 'string' && recipient.avatar.startsWith('http')
              ? { uri: recipient.avatar }
              : initialAvatar;
            return (
              <View key={String(recipient.id)} style={styles.selectedRecipientChip}>
                <Image source={avatarUri} style={styles.chipAvatar} />
                <Text style={styles.chipName}>{recipient.full_name}</Text>
                <TouchableOpacity
                  style={styles.chipRemove}
                  onPress={() => toggleRecipient(recipient)}
                >
                  <Ionicons name="close-circle" size={16} color="#64748B" />
                </TouchableOpacity>
              </View>
            );
          })}
        </ScrollView>
      </View>
    );
  };

  const renderEmpty = () => {
    if (loading) return null;
    return (
      <View style={{ alignItems: 'center', paddingVertical: 40 }}>
        <Ionicons name="people-outline" size={40} color="#CBD5E1" />
        <Text style={{ marginTop: 8, color: '#64748B' }}>
          {error
            ? (error.message || 'Could not search recipients')
            : searchQuery
              ? 'No recipients match your search'
              : 'Start typing to find a recruiter'}
        </Text>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>

        <Text style={styles.headerTitle}>New Message</Text>

        <TouchableOpacity
          style={[
            styles.headerButton,
            (!messageText.trim() || selectedRecipients.length === 0 || sending) && styles.disabledButton,
          ]}
          onPress={sendMessage}
          disabled={!messageText.trim() || selectedRecipients.length === 0 || sending}
        >
          {sending ? (
            <ActivityIndicator size={20} color="#FFFFFF" />
          ) : (
            <Ionicons name="send" size={24} color="#FFFFFF" />
          )}
        </TouchableOpacity>
      </View>

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.container}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
      >
        <View style={styles.searchContainer}>
          <Ionicons name="search" size={20} color="#64748B" style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search recruiters..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            autoCapitalize="none"
            autoCorrect={false}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity style={styles.clearButton} onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color="#64748B" />
            </TouchableOpacity>
          )}
        </View>

        {renderSelectedRecipients()}

        <View style={styles.contentContainer}>
          {loading && recipients.length === 0 ? (
            <View style={{ paddingTop: 40, alignItems: 'center' }}>
              <ActivityIndicator size="large" color="#0e4e96" />
            </View>
          ) : (
            <FlatList
              data={filteredRecipients}
              renderItem={renderRecipientItem}
              keyExtractor={item => String(item.id)}
              contentContainerStyle={styles.recipientsList}
              showsVerticalScrollIndicator={false}
              ListEmptyComponent={renderEmpty}
            />
          )}
        </View>

        <View style={styles.messageInputContainer}>
          <TextInput
            style={styles.messageInput}
            placeholder="Type your message..."
            multiline
            value={messageText}
            onChangeText={setMessageText}
            maxLength={5000}
          />
          <View style={styles.messageActions}>
            <TouchableOpacity style={styles.actionButton}>
              <Ionicons name="attach" size={24} color="#64748B" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.actionButton}>
              <Ionicons name="image-outline" size={24} color="#64748B" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.actionButton}>
              <Ionicons name="document-outline" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#0e4e96',
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  headerButton: {
    padding: 8,
  },
  disabledButton: {
    opacity: 0.5,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    marginHorizontal: 16,
    marginVertical: 12,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 10,
    fontSize: 16,
  },
  clearButton: {
    padding: 4,
  },
  selectedRecipientsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  selectedRecipientsLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#334155',
    marginRight: 8,
  },
  selectedRecipientsList: {
    paddingRight: 16,
  },
  selectedRecipientChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E0F2FE',
    borderRadius: 16,
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginRight: 8,
  },
  chipAvatar: {
    width: 24,
    height: 24,
    borderRadius: 12,
    marginRight: 4,
  },
  chipName: {
    fontSize: 14,
    color: '#0e4e96',
    marginRight: 4,
  },
  chipRemove: {
    padding: 2,
  },
  contentContainer: {
    flex: 1,
  },
  recipientsList: {
    padding: 16,
    paddingTop: 0,
  },
  recipientItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  selectedRecipientItem: {
    backgroundColor: '#F0F9FF',
    borderColor: '#BAE6FD',
  },
  recipientAvatar: {
    position: 'relative',
    marginRight: 12,
  },
  avatarImage: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  onlineIndicator: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#10b981',
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  recipientInfo: {
    flex: 1,
  },
  recipientName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 2,
  },
  recipientRole: {
    fontSize: 14,
    color: '#64748B',
  },
  recipientSelection: {
    marginLeft: 8,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#CBD5E1',
  },
  selectedCheckbox: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#0e4e96',
    justifyContent: 'center',
    alignItems: 'center',
  },
  messageInputContainer: {
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
    padding: 12,
  },
  messageInput: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    maxHeight: 120,
    minHeight: 40,
  },
  messageActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  actionButton: {
    padding: 8,
    marginLeft: 8,
  },
});
